import React, { useState, useEffect } from 'react';
import '../styles/LinkedInPostsAll.css';

const LinkedInPostsAll = () => {
  const [allPosts, setAllPosts] = useState([]);
  const [writers, setWriters] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [writerFilter, setWriterFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const postsPerPage = 10;

  // Fetch writers
  useEffect(() => {
    const fetchWriters = async () => {
      try {
        const response = await fetch('https://n8n.iniai.cloud/webhook/get-linkedin-writers', {
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch writers');
        const data = await response.json();
        
        const formattedWriters = data
          .filter(writer => writer.Name)
          .map(writer => ({
            id: writer.id,
            name: writer.Name
          }));
        
        setWriters(formattedWriters);
      } catch (error) {
        console.error('Error fetching writers:', error);
        setError('Failed to load writers');
      }
    };

    fetchWriters();
  }, []);

  // Fetch posts
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch('https://n8n.iniai.cloud/webhook/get-linkedin-posts-all', {
          headers: {
            'x-api-key': 'auth_k_2f9a8b3c7d6e4f5g9h8i1j2k3l4m5n6'
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch posts');
        const data = await response.json();
        setAllPosts(data);
      } catch (err) {
        console.error('Error fetching posts:', err);
        setError('Failed to load posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Filter posts
  const filteredPosts = allPosts.filter(post => {
    // Status filter
    const matchesStatus = statusFilter === 'all' || 
      post.Status.toLowerCase() === statusFilter.toLowerCase();

    // Writer filter
    let matchesWriter = writerFilter === 'all';
    if (writerFilter !== 'all') {
        const selectedWriter = writers.find(w => w.id.toString() === writerFilter);
        // Use Array.includes to check if the writer's name is in the array.
        matchesWriter = Array.isArray(post['Name (from Writer ID)']) 
          ? post['Name (from Writer ID)'].includes(selectedWriter?.name)
          : post['Name (from Writer ID)'] === selectedWriter?.name;
      }

    return matchesStatus && matchesWriter;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const displayedPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  if (loading) return <div className="loading-state">Loading posts...</div>;
  if (error) return <div className="error-state">{error}</div>;

  return (
    <div className="linkedin-posts-all">
      <div className="posts-header">
        <h1>All LinkedIn Posts</h1>
        <div className="filters">
          <div className="filter-group">
            <label>Status:</label>
            <select 
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="status-filter"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="approved">Approved</option>
              <option value="published">Published</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Writer:</label>
            <select
              value={writerFilter}
              onChange={(e) => {
                setWriterFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="writer-filter"
            >
              <option value="all">All Writers</option>
              {writers.map(writer => (
                <option key={writer.id} value={writer.id}>
                  {writer.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="posts-table">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Author</th>
              <th>Content</th>
              <th>Status</th>
              <th>Score</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedPosts.map(post => (
              <tr key={post['Content Request ID']}>
                <td>
                  {new Date(post['Generated Date']).toLocaleDateString()}
                  {post['Edited Date'] && (
                    <div className="edited-date">
                      Edited: {new Date(post['Edited Date']).toLocaleDateString()}
                    </div>
                  )}
                </td>
                <td>{post['Name (from Writer ID)']}</td>
                <td className="content-cell">
                  {post['Generated Content'].substring(0, 100)}...
                </td>
                <td>
                  <span className={`status-badge ${post.Status.toLowerCase()}`}>
                    {post.Status}
                  </span>
                </td>
                <td>{post['Style Validation Score']}</td>
                <td>
                  <button 
                    onClick={() => window.location.href = `/posts/edit/${post['Content Request ID']}`}
                    className="edit-button"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="pagination-info">
          Showing {startIndex + 1} to {Math.min(startIndex + postsPerPage, filteredPosts.length)} of {filteredPosts.length} posts
        </div>
        <div className="pagination-controls">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="page-button"
          >
            Previous
          </button>
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index + 1}
              onClick={() => handlePageChange(index + 1)}
              className={`page-button ${currentPage === index + 1 ? 'active' : ''}`}
            >
              {index + 1}
            </button>
          ))}
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="page-button"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkedInPostsAll;